import {
  useRef,
  useCallback,
  useMemo,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  type ReactNode,
  type RefObject,
} from 'react';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { ChatBubbleOutlineIcon } from '@/components/ui/Icons';
import { CellCommentShellProvider } from './cellCommentShellContext';
import { CellCommentCornerBadge } from './CellCommentCornerBadge';
import { useCellCommentsTable, type ActiveCellCommentThread } from './CellCommentsTableContext';
import './ProjectTableCellCommentShell.css';

export type SessionCommentShellHandle = {
  openRmb: (ev: { clientX: number; clientY: number }) => void;
  /** Закреплённый тред комментариев (как по бейджу / пункту RMB-меню shell). */
  openCommentThread: () => void;
};

interface SessionCommentShellProps {
  sessionId: string;
  /** Подзаголовок в шапке попапа (дата, чтец и т.д.) */
  title: string;
  commentCount: number;
  /** Позиция слота (расписание): absolute top/left/width/height */
  positionStyle?: React.CSSProperties;
  /**
   * Не дублировать контекстное меню на обёртке: пункт «Комментарии» в меню пузыря (SessionBubble).
   */
  hideWrapperContextMenu?: boolean;
  /**
   * Родитель остаётся grid-контейнером (модалка «Прошедшие»), оболочка не создаёт бокс.
   * Нужен `popoverTriggerRef` с тем же элементом, у которого вёрстка ожидает якорь (обычно `li`).
   */
  displayContents?: boolean;
  popoverTriggerRef?: RefObject<HTMLElement | null>;
  className?: string;
  children: ReactNode;
}

/**
 * Оболочка с бейджем комментариев к сессии (тот же UX, что у ячейки проектов, без column_id).
 */
export const SessionCommentShell = forwardRef<SessionCommentShellHandle, SessionCommentShellProps>(
  function SessionCommentShell(
  {
  sessionId,
  title,
  commentCount,
  positionStyle,
  hideWrapperContextMenu = false,
  displayContents = false,
  popoverTriggerRef,
  className,
  children,
  },
  ref
) {
  const { open, notifyThreadUnmount, isPinnedThread, cancelHoverDismiss, scheduleHoverDismiss } =
    useCellCommentsTable();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const anchorRef = (popoverTriggerRef ?? wrapperRef) as RefObject<HTMLElement | null>;

  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(null);

  const hasComments = commentCount > 0;

  const sessionTarget = useMemo(
    () => ({ kind: 'session' as const, sessionId, title }),
    [sessionId, title]
  );

  const threadRefForUnmount = useRef<ActiveCellCommentThread | null>(null);
  threadRefForUnmount.current = {
    target: sessionTarget,
    triggerRef: anchorRef,
    mode: 'pinned',
  };

  useEffect(
    () => () => {
      const t = threadRefForUnmount.current;
      if (t) notifyThreadUnmount(t);
    },
    [sessionId, title, notifyThreadUnmount]
  );

  const openThread = useCallback(() => {
    open({ target: sessionTarget, triggerRef: anchorRef, mode: 'pinned' });
  }, [open, sessionTarget, anchorRef]);

  const openRmb = useCallback((ev: { clientX: number; clientY: number }) => {
    setContextMenuPos({ x: ev.clientX, y: ev.clientY });
    setContextMenuOpen(true);
  }, []);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    openRmb(e);
  }, [openRmb]);

  useImperativeHandle(
    ref,
    () => ({
      openRmb,
      openCommentThread: openThread,
    }),
    [openRmb, openThread]
  );

  useEffect(() => {
    if (import.meta.env.DEV && displayContents && !popoverTriggerRef) {
      console.warn('SessionCommentShell: popoverTriggerRef is required when displayContents is set');
    }
  }, [displayContents, popoverTriggerRef]);

  /** Скрываем onContextMenu на боксe оболочки; в модалке RMB вешается на `li` + openRmb. */
  const effectiveHideWrapperRmb = hideWrapperContextMenu || displayContents;
  /** У SessionBubble `hide` — тред только из меню пузыря; в модалке с `displayContents` меню shell нужно. */
  const showShellContextMenu = !hideWrapperContextMenu || displayContents;

  const shellPositionStyle: React.CSSProperties = positionStyle
    ? { position: 'absolute', boxSizing: 'border-box', ...positionStyle }
    : { position: 'relative' };

  const handleBadgeOpen = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      cancelHoverDismiss();
      open({ target: sessionTarget, triggerRef: anchorRef, mode: 'pinned' });
    },
    [cancelHoverDismiss, open, sessionTarget, anchorRef]
  );

  const handleBadgePointerEnter = useCallback(() => {
    cancelHoverDismiss();
    if (isPinnedThread(sessionTarget)) return;
    open({ target: sessionTarget, triggerRef: anchorRef, mode: 'hover' });
  }, [cancelHoverDismiss, isPinnedThread, open, sessionTarget, anchorRef]);

  const handleBadgePointerLeave = useCallback(() => {
    scheduleHoverDismiss();
  }, [scheduleHoverDismiss]);

  const commentMenuLabel = hasComments ? 'Посмотреть комментарии' : 'Добавить комментарий';

  const shellContextItems = useMemo(
    () => [
      {
        label: commentMenuLabel,
        icon: <ChatBubbleOutlineIcon size={18} />,
        onClick: openThread,
      },
    ],
    [openThread, commentMenuLabel]
  );

  const commentBadgeInfo = useMemo(
    () => hasComments && positionStyle
      ? {
          count: commentCount,
          onOpen: handleBadgeOpen,
          onPointerEnter: handleBadgePointerEnter,
          onPointerLeave: handleBadgePointerLeave,
        }
      : null,
    [hasComments, positionStyle, commentCount, handleBadgeOpen, handleBadgePointerEnter, handleBadgePointerLeave]
  );

  const contextValue = useMemo(
    () => ({
      addCommentMenuItem: {
        label: commentMenuLabel,
        icon: <ChatBubbleOutlineIcon size={18} />,
        onClick: openThread,
      },
      commentBadge: commentBadgeInfo,
    }),
    [openThread, commentMenuLabel, commentBadgeInfo]
  );

  return (
    <CellCommentShellProvider value={contextValue}>
      <div
        ref={wrapperRef}
        className={[
          'project-table-cell-comment-shell',
          positionStyle ? 'project-table-cell-comment-shell--schedule-slot' : '',
          displayContents ? 'project-table-cell-comment-shell--display-contents' : '',
          className ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={displayContents ? undefined : shellPositionStyle}
        onContextMenu={effectiveHideWrapperRmb ? undefined : handleContextMenu}
      >
        {children}
        {hasComments && !positionStyle ? (
          <CellCommentCornerBadge
            onOpen={handleBadgeOpen}
            onPointerEnter={handleBadgePointerEnter}
            onPointerLeave={handleBadgePointerLeave}
          />
        ) : null}
        {showShellContextMenu ? (
          <ContextMenu
            isOpen={contextMenuOpen}
            onClose={() => setContextMenuOpen(false)}
            anchorPosition={contextMenuPos}
            items={shellContextItems}
          />
        ) : null}
      </div>
    </CellCommentShellProvider>
  );
  }
);

SessionCommentShell.displayName = 'SessionCommentShell';
